(() => {
  const tagName = 'dga-featured-artists-loader';
  if (customElements.get(tagName)) return;
  const origin = 'https://marketplace.darkgloomyart.com';
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const element = (tag, name, text) => {
    const el = document.createElement(tag);
    el.className = `dga-featured-artists__${name}`;
    if (text) el.textContent = text;
    return el;
  };

  class FeaturedArtistsLoader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-featured-artists]');
      this.grid = this.section.querySelector('[data-artists-grid]');
      this.onVisibility = () => {
        if (document.visibilityState === 'visible' && Date.now() >= this.expires) this.load();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.load();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    async card(artist) {
      if (!uuid.test(artist.id) || typeof artist.name !== 'string' || !artist.name.trim())
        return null;
      let imageUrl;
      try {
        imageUrl = new URL(artist.imageUrl);
        if (imageUrl.protocol !== 'https:') return null;
      } catch (_) {
        return null;
      }
      const article = element('article', 'card');
      const link = element('a', 'link');
      link.href = `${origin}/u/${artist.id}`;
      const portrait = element('div', 'portrait');
      const image = element('img', 'image');
      image.alt = '';
      image.width = 640;
      image.height = 640;
      image.decoding = 'async';
      image.src = imageUrl.href;
      // Do not show broken photos or substitute invented portraits.
      try {
        await Promise.race([
          image.decode(),
          new Promise((_, reject) => {
            const timer = setTimeout(() => reject(new Error('Image timeout')), 10000);
            image.addEventListener('load', () => clearTimeout(timer), { once: true });
            image.addEventListener('error', () => clearTimeout(timer), { once: true });
          }),
        ]);
      } catch (_) {
        return null;
      }
      portrait.append(image);
      const caption = element('div', 'caption');
      const details = element('div', 'details');
      details.append(element('h3', 'name', artist.name));
      if (artist.location) details.append(element('p', 'location', artist.location));
      const arrow = element('span', 'arrow', '↗');
      arrow.setAttribute('aria-hidden', 'true');
      caption.append(details, arrow);
      link.append(portrait, caption, element('span', 'cta', this.section.dataset.profileLabel));
      article.append(link);
      return article;
    }

    async load() {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      clearTimeout(this.timer);
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 12000);
      try {
        const url = new URL(this.section.dataset.feedUrl);
        if (
          url.protocol !== 'https:' &&
          url.hostname !== 'localhost' &&
          url.hostname !== '127.0.0.1'
        )
          throw new Error('Invalid feed');
        const response = await fetch(url.href, {
          credentials: 'omit',
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error('Feed unavailable');
        const payload = await response.json();
        if (!Array.isArray(payload.artists)) throw new Error('Invalid artists');
        const seen = new Set();
        const artists = payload.artists
          .filter(a => {
            if (!a || seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          })
          .slice(0, 3);
        const cards = (await Promise.all(artists.map(a => this.card(a)))).filter(Boolean);
        if (!this.isConnected || this.controller.signal.aborted) throw new Error('Load cancelled');
        this.grid.replaceChildren(...cards);
        this.grid.dataset.count = String(cards.length);
        this.section.hidden = cards.length === 0;
        this.expires = Date.parse(payload.nextRotation);
        if (!Number.isFinite(this.expires) || this.expires <= Date.now())
          this.expires = Date.now() + 300000;
      } catch (_) {
        this.section.hidden = true;
        this.grid.replaceChildren();
        this.expires = Date.now() + 60000;
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        if (this.isConnected)
          this.timer = setTimeout(
            () => this.load(),
            Math.min(86400000, Math.max(1000, this.expires - Date.now() + 1000))
          );
      }
    }
  }
  customElements.define(tagName, FeaturedArtistsLoader);
  const init = root =>
    root.querySelectorAll('[data-featured-artists]').forEach(section => {
      if (!section.querySelector(tagName)) section.append(document.createElement(tagName));
    });
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
